import axios from 'axios';

import { CONFIG } from 'src/global-config';
import axiosInstance, { endpoints } from 'src/lib/axios-artham';

// Local storage keys
const FCM_TOKEN_KEY = 'fcm_device_token';
const DEVICE_ID_KEY = 'fcm_device_id';

// Public axios (no auth required) for guest device operations
const publicAxios = axios.create({
  baseURL: CONFIG.serverUrl,
  headers: { 'Content-Type': 'application/json' },
});

// ----------------------------------------------------------------------
// API Functions
// ----------------------------------------------------------------------

/**
 * Register device token with backend.
 * Backend `POST /fcm/tokens` inserts or updates the token row and associates
 * it with the authenticated user automatically (via the auth middleware).
 */
export async function registerDeviceToken(
  token: string,
  platform: string,
  deviceId: string
): Promise<void> {
  // Use authed axios when a session exists so the token is linked to the user;
  // fall back to public axios for pre-login device identification.
  const client = axiosInstance.defaults.headers.common.Authorization ? axiosInstance : publicAxios;
  await client.post(endpoints.fcm.tokens, { token, platform, deviceId });
}

/**
 * No-op shim kept for backwards compatibility with `use-fcm.ts`.
 * The backend associates tokens with the authenticated user at registration
 * time, so a separate "link" call is unnecessary.
 */
export async function linkDeviceToUser(_deviceId: string): Promise<void> {
  return Promise.resolve();
}

/**
 * Deactivate device token on logout.
 * Backend `DELETE /fcm/tokens/:token` removes or soft-deletes the row.
 */
export async function deactivateDeviceToken(token: string, _deviceId?: string): Promise<void> {
  await publicAxios.delete(endpoints.fcm.tokenById(encodeURIComponent(token))).catch(() => {});
}

// ----------------------------------------------------------------------
// Local Storage Helpers
// ----------------------------------------------------------------------

export function storeFCMTokenLocally(token: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(FCM_TOKEN_KEY, token);
  }
}

export function getStoredFCMToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(FCM_TOKEN_KEY);
}

export function clearStoredFCMToken(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(FCM_TOKEN_KEY);
  }
}

export function storeDeviceIdLocally(deviceId: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
}

export function getStoredDeviceId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(DEVICE_ID_KEY);
}

export function clearStoredDeviceId(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(DEVICE_ID_KEY);
  }
}
